import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoLibraryDropTargetProps {
  onFiles: (files: File[]) => void;
  children: ReactNode;
}

const OvVideoLibraryDropTarget: FC<OvVideoLibraryDropTargetProps> = ({
  onFiles,
  children,
}) => {
  const { t } = useTranslation();
  const { getRootProps, isDragActive } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: (accepted, rejected) =>
      onFiles([...accepted, ...rejected.map(({ file }) => file)]),
  });

  return (
    <Target {...getRootProps({ tabIndex: -1 })}>
      {children}
      {isDragActive && (
        <Overlay>
          <CloudUploadIcon aria-hidden="true" />
          {t('videos.staging.libraryDrop')}
        </Overlay>
      )}
    </Target>
  );
};

export default OvVideoLibraryDropTarget;

const Target = styled.div`
  position: relative;

  &:focus {
    outline: none;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: 2px dashed ${Colours.OV_ACCENT_BLUE};
  border-radius: ${Variables.borderRadius.LARGE};
  background-color: ${Colours.WHITE};
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
  opacity: 0.95;
  pointer-events: none;
`;
